/**
 * Keeps only the latest call's answer. Requests can finish out of order (a slow answer for old
 * input after a fast one for new input); stores fed from `done` and `fail` never go back to an
 * older answer.
 *
 * Call `run` instead of the effect itself. Each call is tagged, so even two calls with the same
 * params (reloading the same position before and after a change) are told apart. The wrapped
 * effect still runs, so its `pending` keeps working.
 */
import {
  attach,
  createEffect,
  createStore,
  sample,
  type Effect,
  type Event,
  type Unit,
} from 'effector';

export interface Latest<Params, Done, Fail> {
  run: Effect<Params, Done, Fail>;
  /** The latest call's result, once it arrives. */
  done: Event<Done>;
  /** The latest call's error. */
  fail: Event<Fail>;
}

export function latest<Params, Done, Fail = Error>(
  effect: Effect<Params, Done, Fail>,
  options: { reset?: Unit<unknown> } = {},
): Latest<Params, Done, Fail> {
  const tagged = createEffect<{ params: Params }, Done, Fail>(({ params }) => effect(params));
  const run = attach({ effect: tagged, mapParams: (params: Params) => ({ params }) });
  const $call = createStore<{ params: Params } | null>(null, { skipVoid: false }).on(
    tagged,
    (_, call) => call,
  );
  if (options.reset) $call.reset(options.reset);
  const done = sample({
    clock: tagged.done,
    source: $call,
    filter: (call, { params }) => call === params,
    fn: (_, { result }) => result,
  });
  const fail = sample({
    clock: tagged.fail,
    source: $call,
    filter: (call, { params }) => call === params,
    fn: (_, { error }) => error,
  });
  return { run, done, fail };
}
